/**
 * EntityMessageBoard — Facebook-style discussion board that any page
 * can attach to via an (entityType, entityId) pair.
 *
 * Rich composer:
 *   • Post-type dropdown: Comment · Addition/New Post · Correction · Suggestion · Other
 *   • Free-form text (up to 3000 chars)
 *   • Attach up to 6 images (each ≤ 5 MB, jpg/png/webp/heic)
 *   • Attach one YouTube / Vimeo video URL (rendered as embed)
 *   • Attach one external link URL (rendered as a preview card)
 *
 * Guests see a "Sign in to join" nudge. Members can delete their
 * own posts; admins can delete anyone's.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Image as ImageIcon, Video, Link as LinkIcon, X,
  MessageCircle, Send, Trash2, Loader2, LogIn, UserRound,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/* ─── small utilities ─────────────────────────────────────────────── */
const POST_TYPES = [
  { key: "comment",     label: "Comment",             chip: "#334155" },
  { key: "addition",    label: "Addition / New Post", chip: "#059669" },
  { key: "correction",  label: "Correction",          chip: "#DC2626" },
  { key: "suggestion",  label: "Suggestion",          chip: "#2563EB" },
  { key: "other",       label: "Other",               chip: "#7C3AED" },
];
const REACTIONS = [
  { key: "thumbs_up", emoji: "👍", label: "Like" },
  { key: "heart",     emoji: "❤️", label: "Love" },
  { key: "pray",      emoji: "🙏", label: "Thank you" },
  { key: "laugh",     emoji: "😂", label: "Haha" },
  { key: "sad",       emoji: "😢", label: "Sad" },
  { key: "think",     emoji: "🤔", label: "Hmm" },
];
const MAX_IMAGES  = 6;
const MAX_BYTES   = 5 * 1024 * 1024;
const API_ORIGIN  = process.env.REACT_APP_BACKEND_URL || "";

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30)    return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

// Some upload endpoints return an app-relative /api/... path — turn
// that into an absolute URL so <img> tags can load it.
const toAbsoluteUrl = (u) =>
  !u ? "" : /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`;

// Extract a YouTube or Vimeo video id from a URL. Deliberately loose
// so paste-anywhere still works.
function parseVideoEmbed(u) {
  if (!u) return null;
  try {
    const url = new URL(u);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.slice(1);
      if (id) return { src: `https://www.youtube.com/embed/${id}`, kind: "youtube" };
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = url.searchParams.get("v");
      if (v) return { src: `https://www.youtube.com/embed/${v}`, kind: "youtube" };
      const m = url.pathname.match(/^\/(shorts|embed|v|live)\/([A-Za-z0-9_-]+)/);
      if (m) return { src: `https://www.youtube.com/embed/${m[2]}`, kind: "youtube" };
    }
    if (host.endsWith("vimeo.com")) {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return { src: `https://player.vimeo.com/video/${id}`, kind: "vimeo" };
    }
  } catch { /* fallthrough */ }
  return null;
}

export default function EntityMessageBoard({ entityType, entityId, title = "Discussion" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ─── composer state ────────────────────────────────────────────── */
  const [postType, setPostType] = useState("comment");
  const [text, setText]         = useState("");
  const [images, setImages]     = useState([]);     // uploaded URLs
  const [videoUrl, setVideoUrl] = useState("");
  const [linkUrl, setLinkUrl]   = useState("");
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [showLinkInput,  setShowLinkInput]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posting,   setPosting]   = useState(false);
  const fileRef = useRef(null);

  const resetComposer = () => {
    setPostType("comment");
    setText("");
    setImages([]);
    setVideoUrl("");
    setLinkUrl("");
    setShowVideoInput(false);
    setShowLinkInput(false);
  };

  /* ─── data load ─────────────────────────────────────────────────── */
  const load = () => {
    setLoading(true);
    http.get(`/entity/${entityType}/${entityId}/messages?limit=100`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (entityType && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  /* ─── image upload flow (per-file) ──────────────────────────────── */
  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = MAX_IMAGES - images.length;
    if (list.length > remaining) {
      toast.error(`You can attach at most ${MAX_IMAGES} images per post.`);
    }
    setUploading(true);
    const uploaded = [];
    for (const f of list.slice(0, remaining)) {
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" is too large (max 5 MB)`);
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
      try {
        const r = await http.post(`/entity/upload`, { image: dataUrl });
        if (r.data?.url) uploaded.push(r.data.url);
      } catch (e) {
        toast.error(`Upload failed for ${f.name}`);
      }
    }
    setImages((prev) => [...prev, ...uploaded].slice(0, MAX_IMAGES));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ─── post ──────────────────────────────────────────────────────── */
  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { toast.error("Sign in to leave a message"); return; }
    const body = text.trim();
    if (!body && !images.length && !videoUrl.trim() && !linkUrl.trim()) return;
    setPosting(true);
    try {
      const payload = {
        body:      body || " ",
        post_type: postType,
        images,
        video_url: videoUrl.trim() || null,
        link_url:  linkUrl.trim()  || null,
      };
      const r = await http.post(`/entity/${entityType}/${entityId}/messages`, payload);
      setItems((prev) => [r.data, ...prev]);
      resetComposer();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post your message. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    if (!id || !window.confirm("Delete this message?")) return;
    try {
      await http.delete(`/entity/messages/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
    } catch {
      toast.error("Could not delete this message");
    }
  };

  const react = async (id, emoji) => {
    if (!user) { toast.error("Sign in to react"); return; }
    // Optimistic update — flip the emoji locally and rollback on failure.
    setItems((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const mine = new Set(m.my_reactions || []);
      const counts = { ...(m.reaction_counts || {}) };
      if (mine.has(emoji)) {
        mine.delete(emoji);
        counts[emoji] = Math.max(0, (counts[emoji] || 1) - 1);
      } else {
        mine.add(emoji);
        counts[emoji] = (counts[emoji] || 0) + 1;
      }
      return { ...m, my_reactions: [...mine], reaction_counts: counts };
    }));
    try {
      const r = await http.post(`/entity/messages/${id}/react`, { emoji });
      setItems((prev) => prev.map((m) => m.id === id ? { ...m, reaction_counts: r.data.reaction_counts, my_reactions: r.data.my_reactions } : m));
    } catch (e) {
      toast.error("Could not save your reaction — try again.");
      // Refetch to sync back.
      load();
    }
  };

  const chip = POST_TYPES.find((p) => p.key === postType) || POST_TYPES[0];

  return (
    <section
      className="mt-8 rounded-3xl border p-5 sm:p-6"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
      data-testid={`entity-board-${entityType}-${entityId}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
        <div className="font-display text-xl">{title}</div>
        <span className="ml-2 text-xs font-mono opacity-60">
          {items.length ? `${items.length} post${items.length === 1 ? "" : "s"}` : "be the first"}
        </span>
      </div>
      <p className="text-xs opacity-70">
        Share notes, corrections, or add local knowledge. Text · Images · Videos · Links.
      </p>

      {/* ─── Composer ─── */}
      <form onSubmit={submit} className="mt-4">
        {user ? (
          <div className="rounded-2xl border p-3 sm:p-4" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center shrink-0 overflow-hidden">
                {user.avatar_url
                  ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  : <UserRound className="w-4 h-4 opacity-60" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <label className="text-[10px] font-mono uppercase tracking-widest opacity-60">Post type</label>
                  <select
                    value={postType}
                    onChange={(e) => setPostType(e.target.value)}
                    className="text-xs font-semibold px-2 py-1 rounded-full border focus:outline-none"
                    style={{ borderColor: chip.chip, color: chip.chip, background: "#fff" }}
                    data-testid="entity-board-post-type"
                  >
                    {POST_TYPES.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a post…"
                  rows={3}
                  maxLength={3000}
                  className="w-full text-sm rounded-xl border px-3 py-2 bg-transparent resize-none"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="entity-board-input"
                />
                {/* Attachments strip */}
                {(images.length > 0 || videoUrl || linkUrl) && (
                  <div className="mt-3 space-y-3">
                    {images.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="composer-images">
                        {images.map((u, i) => (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                            <img src={toAbsoluteUrl(u)} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                              className="absolute top-1 right-1 rounded-full bg-black/70 text-white p-1"
                              aria-label="Remove image"
                              data-testid={`composer-image-remove-${i}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {showVideoInput && (
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="YouTube or Vimeo URL"
                        className="w-full text-sm rounded-lg border px-3 py-2"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid="composer-video-input"
                      />
                    )}
                    {showLinkInput && (
                      <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Paste a link URL"
                        className="w-full text-sm rounded-lg border px-3 py-2"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid="composer-link-input"
                      />
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/heic,image/gif"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                      data-testid="composer-file-input"
                    />
                    <IconButton
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading || images.length >= MAX_IMAGES}
                      label={`Add photo (${images.length}/${MAX_IMAGES})`}
                      icon={ImageIcon}
                      tint="#0284C7"
                      testId="composer-photo-btn"
                    />
                    <IconButton
                      onClick={() => setShowVideoInput((v) => !v)}
                      label="Attach video"
                      icon={Video}
                      tint="#DC2626"
                      testId="composer-video-btn"
                    />
                    <IconButton
                      onClick={() => setShowLinkInput((v) => !v)}
                      label="Attach link"
                      icon={LinkIcon}
                      tint="#7C3AED"
                      testId="composer-link-btn"
                    />
                    <span className="ml-2 text-[11px] opacity-60">{text.length}/3000</span>
                    {uploading && <span className="text-[11px] opacity-70 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>}
                  </div>
                  <button
                    type="submit"
                    disabled={posting || (!text.trim() && !images.length && !videoUrl.trim() && !linkUrl.trim())}
                    className="wofy-btn-primary text-sm"
                    data-testid="entity-board-submit"
                  >
                    {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {posting ? "Posting…" : "Post"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl border p-4 flex items-center justify-between gap-3 flex-wrap"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          >
            <div className="text-sm">
              <div className="font-medium">Sign in to join the conversation</div>
              <div className="text-xs opacity-70 mt-0.5">
                Members-only board — keeps discussions high-quality.
              </div>
            </div>
            <Link to="/login" className="wofy-btn-primary text-sm" data-testid="entity-board-signin">
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </Link>
          </div>
        )}
      </form>

      {/* ─── Thread ─── */}
      <div className="mt-6 space-y-5" data-testid="entity-board-thread">
        {loading && <div className="text-sm opacity-60"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm opacity-60 text-center py-6">
            No messages yet. Kick things off — your community is listening.
          </div>
        )}
        {!loading && items.map((m) => (
          <MessageItem key={m.id} m={m} onDelete={() => remove(m.id)} onReact={(emoji) => react(m.id, emoji)} canReact={!!user} />
        ))}
      </div>
    </section>
  );
}

/* ─── message renderer ─────────────────────────────────────────────── */
function MessageItem({ m, onDelete, onReact, canReact }) {
  const chip = POST_TYPES.find((p) => p.key === m.post_type) || POST_TYPES[0];
  const embed = parseVideoEmbed(m.video_url);
  const mine = new Set(m.my_reactions || []);
  const counts = m.reaction_counts || {};
  return (
    <article
      className="flex items-start gap-3"
      data-testid={`entity-board-msg-${m.id}`}
    >
      <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center shrink-0 overflow-hidden">
        {m.user_avatar
          ? <img src={m.user_avatar} alt="" className="w-full h-full object-cover" />
          : <UserRound className="w-4 h-4 opacity-60" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-medium truncate">{m.user_name}</span>
          <span className="opacity-50">· {relTime(m.created_at)}</span>
          {m.is_mine && (
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">you</span>
          )}
          {m.post_type && m.post_type !== "comment" && (
            <span
              className="text-[10px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5"
              style={{ background: `${chip.chip}18`, color: chip.chip }}
            >
              {chip.label}
            </span>
          )}
        </div>
        <div className="mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words">
          {m.body}
        </div>
        {Array.isArray(m.images) && m.images.length > 0 && (
          <div className={`mt-2 grid gap-1 ${m.images.length === 1 ? "" : "grid-cols-3"}`} data-testid={`msg-images-${m.id}`}>
            {m.images.slice(0, 6).map((u, i) => (
              <a
                key={i}
                href={toAbsoluteUrl(u)}
                target="_blank"
                rel="noreferrer"
                className={`overflow-hidden rounded-lg border ${m.images.length === 1 ? "" : "aspect-square"}`}
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <img
                  src={toAbsoluteUrl(u)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover hover:opacity-90 transition"
                />
              </a>
            ))}
          </div>
        )}
        {embed && (
          <div className="mt-2 rounded-lg overflow-hidden border aspect-video" style={{ borderColor: "var(--wofy-border)" }} data-testid={`msg-video-${m.id}`}>
            <iframe
              src={embed.src}
              title={`${embed.kind} embed`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
        {m.link_url && !embed && (
          <a
            href={m.link_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs hover:bg-black/[0.03] transition"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`msg-link-${m.id}`}
          >
            <LinkIcon className="w-3.5 h-3.5 opacity-60" />
            <span className="truncate">{m.link_url}</span>
          </a>
        )}
        {/* Reaction bar — 6 emojis, hover-brightens, pressed state
            when the current user's already reacted. Counts hidden
            when 0 to keep the row tidy. */}
        <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid={`msg-reactions-${m.id}`}>
          {REACTIONS.map((r) => {
            const active = mine.has(r.key);
            const count  = counts[r.key] || 0;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => onReact?.(r.key)}
                disabled={!canReact}
                title={r.label}
                aria-label={`${r.label} (${count})`}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs transition ${active ? "shadow-inner" : "hover:bg-black/[0.04]"} disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{
                  borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background:  active ? "rgba(245,130,32,0.10)" : "transparent",
                }}
                data-testid={`msg-react-${m.id}-${r.key}`}
              >
                <span className="text-sm leading-none">{r.emoji}</span>
                {count > 0 && <span className="font-mono opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>
      {m.is_mine && (
        <button
          onClick={onDelete}
          title="Delete"
          className="p-1.5 rounded-full text-red-500 hover:bg-red-50 shrink-0"
          data-testid={`entity-board-delete-${m.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </article>
  );
}

/* ─── icon-only composer button ────────────────────────────────────── */
function IconButton({ onClick, disabled, label, icon: Icon, tint, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="p-2 rounded-full hover:bg-black/[0.05] disabled:opacity-40 transition"
      data-testid={testId}
    >
      <Icon className="w-4 h-4" style={{ color: tint }} />
    </button>
  );
}
